'use client'
import classNames from 'classnames'
import Image from 'next/image'
import Link from 'next/link'
import { useParams } from 'next/navigation'
import { type FC } from 'react'
import citiesIcon from './assets/cities.svg'
import styles from './TopCitiesSection.module.scss'
import { citiesKeywordSlug } from '@/city/constants'
import type { City } from '@/country/types'
import { getConfig } from '@/tools/config'
import { Translator } from '@/tools/i18n'
import { useLocalisation } from '@/tools/i18n/client'
import { slugify } from '@/tools/strings/slugify'
import { Button, ButtonVariants } from '@/tools/ui'

type Props = {
  topCities: string[]
  cities: City[]
}

export const TopCitiesSection: FC<Props> = ({ topCities, cities }) => {
  const { country } = useParams()
  const { t, getLanguage } = useLocalisation()
  const { appUrl } = getConfig()

  const language = getLanguage()
  const countryCode = typeof country === 'string' ? country : ''
  const citiesKeywordSlugTranslated = citiesKeywordSlug[language] || citiesKeywordSlug.en

  const getCityName = () =>
    topCities
      .map((cityCode) => cities.find((c) => c.code === cityCode))
      .filter((city): city is City => city !== undefined)

  const filteredCities = getCityName().map((city) => {
    // Some cities names are areas and have a parenthesis with specific cities
    const updatedName = city.name ? city.name.replace(/\s?\(.*\)\s?/g, '') : null
    return { ...city, name: updatedName }
  })

  const buildCityPageLink = (city: City) =>
    city.name ? `${appUrl}/${countryCode?.toLowerCase()}/${language}/${slugify(city.name)}` : null

  const citiesMapLink = `${appUrl}/${country}/${language}/map/${slugify(citiesKeywordSlugTranslated)}/`

  return (
    <section className={styles.wrapper}>
      <div className={styles.topContent}>
        <Image alt={t('common.cities')} src={citiesIcon} />
        <p className={classNames(styles.title, 'pintxo-typography-title1')}>
          <Translator
            keyText="landing.citiesList.title"
            params={{
              country: t(`common.country.${countryCode.toUpperCase()}`),
            }}
          />
        </p>
      </div>
      <div className={styles.container}>
        {filteredCities &&
          filteredCities.map((city) => (
            <div key={city.code}>
              <Link className={styles.link} href={buildCityPageLink(city) ?? '#'}>
                <Button className={styles.country} variant={ButtonVariants.neutral}>
                  {city.name}
                </Button>
              </Link>
            </div>
          ))}
      </div>
      <Link className={styles.button} href={citiesMapLink}>
        <Button variant={ButtonVariants.primary}>{t('seo-landing.city-list.all-cities')}</Button>
      </Link>
    </section>
  )
}
